import { useEffect } from 'react';

interface AdSenseProps {
  clientId?: string;
  enabled?: boolean;
}

const ADSENSE_SCRIPT_ID = 'google-adsense-script';
const ADSENSE_META_SELECTOR = 'meta[name="google-adsense-account"]';
const LOCAL_ADSENSE_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

/**
 * Google AdSense Integration Component
 * 
 * This component injects the Google AdSense script when VITE_ADSENSE_ENABLED is true.
 * It relies on AdSense Auto Ads for placement throughout the app.
 * 
 * Usage:
 * 1. Include <AdSense> once near the top of your app (e.g., in App.tsx)
 * 2. Auto Ads are handled by Google once the script loads
 * 
 * Environment Variables (set in Cloudflare Pages dashboard):
 * - VITE_ADSENSE_ENABLED: "true" to enable ads
 * - VITE_ADSENSE_CLIENT_ID: Your Google AdSense Client ID (ca-pub-xxxx)
 */
export function AdSense({ clientId, enabled = false }: AdSenseProps) {
  useEffect(() => {
    const configuredClientId =
      clientId ||
      document
        .querySelector<HTMLMetaElement>(ADSENSE_META_SELECTOR)
        ?.getAttribute('content') ||
      '';

    const isLocalHost = LOCAL_ADSENSE_HOSTS.has(window.location.hostname);

    // Only load AdSense if enabled, configured, and running on a deployable host.
    if (!enabled || !configuredClientId || isLocalHost) {
      return;
    }

    // Check if AdSense is already loaded
    if (document.getElementById(ADSENSE_SCRIPT_ID)) {
      return;
    }

    // Create and inject AdSense script with better error handling
    const loadScript = () => {
      try {
        const script = document.createElement('script');
        script.id = ADSENSE_SCRIPT_ID;
        script.async = true;
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(configuredClientId)}`;
        script.crossOrigin = 'anonymous';
        script.setAttribute('data-ad-client', configuredClientId);

        // Set timeout to prevent hanging
        const timeout = setTimeout(() => {
          console.warn('AdSense script loading timed out');
        }, 10000);

        script.onload = () => {
          clearTimeout(timeout);
          console.log('Google AdSense loaded successfully');
        };

        script.onerror = () => {
          clearTimeout(timeout);
          console.error('Failed to load Google AdSense');
        };

        // Append to head in a try-catch
        try {
          document.head.appendChild(script);
        } catch (e) {
          clearTimeout(timeout);
          console.error('Failed to append AdSense script to head:', e);
        }
      } catch (e) {
        console.error('Error creating AdSense script:', e);
      }
    };

    // Load script asynchronously, non-blocking
    const timeoutId = setTimeout(loadScript, 0);

    return () => {
      clearTimeout(timeoutId);
      // Note: We don't remove the script on unmount to allow it to finish loading
      // and to avoid removing scripts that might be in use
    };
  }, [enabled, clientId]);

  // This component doesn't render anything visible
  // Errors are logged but don't affect the UI
  return null;
}
